/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2021 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 *
 * @file ResourceDataComponent.jsx
 * @author Srinivasan Bakthavatchalam
 * @since 1.0.1
 */

import React, { Fragment, memo } from 'react';
import PropTypes from 'prop-types';

import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import { withStyles } from '@material-ui/core/styles';
import { injectIntl, intlShape } from 'react-intl';

import ResourceCardView from './cardView/ResourceCardView';
import ResourceListView from './listView/ResourceListView';
import ResourceUtils from '../utils/ResourceUtils';
import { BOOK_LEVEL_RESOURCE, PAGE_LEVEL_RESOURCE } from '../common/constants';
import messages from '../../translations/defaultMessages';

const styles = (theme => ({
  root: {
    background: theme.color.background,
    color: theme.color.textPrimary,
    paddingBottom: 0,
    width: '100%',
    borderRadius: 8
  },
  nested: {
    paddingLeft: theme.spacing(2)
  },
  divider: {
    backgroundColor: theme.color.divider,
    opacity: 0.2
  }
}));

const ResourceDataComponent = ({
  bulkDownloadStatus,
  classes,
  currentPageId,
  isCardView,
  isFilterDropDownOpen,
  intl,
  isSearchAndFilterOff,
  level,
  onResourceClick,
  resourceData,
  resourceLevelTabValue,
  selectedResources,
  setSelectedResources,
  showDownload,
  userRoleTabValue
}) => {
  const { formatMessage } = intl;
  const isSelected = (resourceItem) => {
    if (!showDownload) {
      return false;
    }
    if (!resourceItem.subtopic && selectedResources) {
      const currentResourceId = resourceItem.id;
      const isCurrentResourceSelected = selectedResources.some(resource => resource.id === currentResourceId);

      if (isCurrentResourceSelected) {
        return true;
      }
    } else if (resourceItem.subtopic && selectedResources) {
      // subtopic represne that it is a book level resource and parent level resource
      const flatResource = ResourceUtils.constructFlatData(resourceItem.subtopic);
      const downloadableFlatResource = flatResource.filter(resource => ResourceUtils.isResourceDownloadable(resource));

      if (downloadableFlatResource.length <= selectedResources.length) {
      // if less length there may be possiblities that all the child are selected.
        const selectedIds = selectedResources.map(resource => resource.id);
        const findAllValues = downloadableFlatResource.filter(resource => !selectedIds.includes(resource.id));

        if (findAllValues.length === 0) {
          return true;
        }
      }
    }

    return false;
  };

  return (
    isCardView ?
      <Fragment>
        <div className="resource-cards">
          {resourceLevelTabValue === BOOK_LEVEL_RESOURCE &&
          isSearchAndFilterOff ? Object.entries(resourceData).map((resourceItem, index) => (
            <Fragment key={`resource-${index}-${level}-${resourceLevelTabValue}-${userRoleTabValue}-${resourceItem[0]}`}>
              <div
                className="resource-card-category"
              >
                <div className="resource-card-category-heading">
                  {formatMessage(messages[resourceItem[0].split(' ').join('_').toLowerCase()])}
                </div>
                <ResourceCardView
                  bulkDownloadStatus={bulkDownloadStatus}
                  categoryItems={resourceItem[1]}
                  isFilterDropDownOpen={isFilterDropDownOpen}
                  onResourceClick={onResourceClick}
                  selectedResources={selectedResources}
                  setSelectedResources={setSelectedResources}
                  showDownload={showDownload}
                />
              </div>
              <Divider className={classes.divider} />
            </Fragment>
            )) :
          <div
            className="resource-card-category"
          >
            <ResourceCardView
              bulkDownloadStatus={bulkDownloadStatus}
              categoryItems={resourceData}
              currentPageId={currentPageId}
              isFilterDropDownOpen={isFilterDropDownOpen}
              isPageLevel={resourceLevelTabValue === PAGE_LEVEL_RESOURCE}
              onResourceClick={onResourceClick}
              selectedResources={selectedResources}
              setSelectedResources={setSelectedResources}
              showDownload={showDownload}
            />
          </div>
          }
        </div>
      </Fragment>
      :
      <List
        aria-labelledby="resource-list"
        className={classes.root}
        component="nav"
      >
        {resourceData && resourceData.map((resourceItem, index) => (
          <ResourceListView
            bulkDownloadStatus={bulkDownloadStatus}
            currentPageId={currentPageId}
            indexVal={index}
            isFilterDropDownOpen={isFilterDropDownOpen}
            isLastItem={index === (resourceData.length - 1)}
            isSearchAndFilterOff={isSearchAndFilterOff}
            isSelected={isSelected(resourceItem)}
            key={`resource-${index}-${level}-${resourceLevelTabValue}-${userRoleTabValue}-id-${resourceItem.id || resourceItem.name}`}
            level={level}
            onResourceClick={onResourceClick}
            resourceItem={resourceItem}
            resourceLevelTabValue={resourceLevelTabValue}
            selectedResources={selectedResources}
            setSelectedResources={setSelectedResources}
            showDownload={showDownload}
            userRoleTabValue={userRoleTabValue}
          />
        ))}
      </List>
  );
};

ResourceDataComponent.propTypes = {
  bulkDownloadStatus: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
  currentPageId: PropTypes.array.isRequired,
  isCardView: PropTypes.bool.isRequired,
  isFilterDropDownOpen: PropTypes.bool.isRequired,
  isSearchAndFilterOff: PropTypes.bool.isRequired,
  level: PropTypes.number,
  onResourceClick: PropTypes.func.isRequired,
  resourceData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  resourceLevelTabValue: PropTypes.string.isRequired,
  selectedResources: PropTypes.array.isRequired,
  setSelectedResources: PropTypes.func.isRequired,
  showDownload: PropTypes.bool.isRequired,
  userRoleTabValue: PropTypes.string.isRequired,
  intl: intlShape
};
ResourceDataComponent.defaultProps = {
  level: 1,
  resourceData: [],
  intl: {}
};

export default withStyles(styles)(injectIntl(memo(ResourceDataComponent)));
